import jsPDF from "jspdf";

// Palette
const INK = [26, 26, 24];
const MUTED = [92, 90, 85];
const BORDER = [215, 210, 203];
const ACCENT = [166, 93, 71];

const CAT_CONFIG = {
  proteici: { label: "Proteici", rgb: [166, 93, 71] },
  amidacei: { label: "Amidacei", rgb: [139, 115, 85] },
  frutta:   { label: "Frutta",   rgb: [176, 140, 77] },
  lipidici: { label: "Lipidici", rgb: [99, 112, 91] },
};
const CAT_ORDER = ["proteici", "amidacei", "frutta", "lipidici"];

const setColor = (doc, rgb) => { doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setDrawColor(rgb[0], rgb[1], rgb[2]); };
const setFill = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);

// A4 layout — tuned to fit the entire porzionario + legend on a single page.
const PAGE_W = 210, PAGE_H = 297;
const M_X = 10;
const CONTENT_W = PAGE_W - M_X * 2;
const COL_GAP = 5;
const COL_W = (CONTENT_W - COL_GAP) / 2;
const LEGEND_H = 12;
const FOOTER_H = 5;
const TOP_Y = 30;
const BOTTOM_Y = PAGE_H - LEGEND_H - FOOTER_H - 2;

// Slightly enlarged sizing (was LINE_H 3.5 / font 7pt).
const LINE_H = 4.2;
const HEADER_H = 7.5;
const CAT_TOP_PAD = 2.5;
const CAT_BOTTOM_PAD = 1.5;

// ------------------ page chrome ------------------
function drawPageHeader(doc, { studio, gender }) {
  let y = 8;
  const hasStudio = !!(studio && (studio.studio_name || studio.address || studio.phone || studio.email || studio.website));
  if (hasStudio) {
    setColor(doc, INK);
    doc.setFont("times", "normal"); doc.setFontSize(10.5);
    if (studio.studio_name) { doc.text(studio.studio_name, M_X, y + 3.6); y += 3.9; }
    setColor(doc, MUTED);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7);
    const contacts = [studio.address, studio.phone, studio.email, studio.website].filter(Boolean).join(" · ");
    if (contacts) { doc.text(contacts, M_X, y + 3); y += 3.2; }
    setColor(doc, BORDER); doc.setLineWidth(0.2); doc.line(M_X, y + 2, M_X + CONTENT_W, y + 2);
    y += 3.5;
  } else {
    y = 10;
  }

  setFill(doc, ACCENT); doc.rect(M_X, y - 1, 16, 0.9, "F");
  setColor(doc, MUTED); doc.setFont("helvetica", "bold"); doc.setFontSize(7);
  doc.text("PORZIONARIO · EQUIVALENTI ALIMENTARI", M_X, y + 3);
  const genderLabel = gender === "F" ? "Tabella femminile" : "Tabella maschile";
  doc.text(genderLabel, M_X + CONTENT_W - doc.getTextWidth(genderLabel), y + 3);
}

function drawFooter(doc, { studio }) {
  setColor(doc, MUTED);
  doc.setFont("helvetica", "italic"); doc.setFontSize(7);
  const brand = studio?.studio_name || "Metodo Funzionale";
  doc.text(`${brand} · Porzionario`, M_X, PAGE_H - 3.5);
}

// Fixed macro description per gender × category — replaces the old
// per-portion kcal indicator. Values are the coach's calibration targets
// (Metodo Funzionale).
const PORTION_MACRO_TEXT = {
  M: {
    proteici: "1 porzione \u00B1 15 gr di proteine",
    amidacei: "1 porzione \u00B1 15 gr di carboidrati complessi",
    frutta:   "1 porzione \u00B1 10 gr di carboidrati semplici",
    lipidici: "1 porzione \u00B1 10 gr di grassi",
  },
  F: {
    proteici: "1 porzione \u00B1 10 gr di proteine",
    amidacei: "1 porzione \u00B1 12 gr di carboidrati complessi",
    frutta:   "1 porzione \u00B1 10 gr di carboidrati semplici",
    lipidici: "1 porzione \u00B1 8 gr di grassi",
  },
};

function drawCategoryHeader(doc, catKey, x, y, w, gender) {
  const cat = CAT_CONFIG[catKey];
  setFill(doc, cat.rgb); doc.rect(x, y - 4, 2.4, HEADER_H, "F");
  setColor(doc, INK); doc.setFont("times", "normal"); doc.setFontSize(12);
  doc.text(cat.label, x + 5, y + 0.8);
  const titleW = doc.getTextWidth(cat.label);
  const macroText = PORTION_MACRO_TEXT[gender === "F" ? "F" : "M"]?.[catKey] || "";
  setColor(doc, MUTED); doc.setFont("helvetica", "italic"); doc.setFontSize(7);
  doc.text(`· ${macroText}`, x + 5 + titleW + 1.5, y + 0.8);
  setColor(doc, BORDER); doc.setLineWidth(0.2);
  doc.line(x + 5, y + 3, x + w, y + 3);
}

function drawFoodRow(doc, food, x, y, w) {
  const rawName = food.name || "";
  const m = rawName.match(/^(.*?)\s+2\s*$/);
  const displayNameRaw = m ? m[1] : rawName;
  // jsPDF's built-in Helvetica uses WinAnsi encoding: characters outside it
  // (e.g. `≥` U+2265) get rendered with awkward letter-spacing and stray
  // replacement chars. Substitute with ASCII equivalents for the PDF only.
  const displayName = displayNameRaw
    .replace(/\u2265/g, ">=")
    .replace(/\u2264/g, "<=");
  const isEgg = /^uov[oa]\s+inter/i.test(rawName);
  const gLabel = isEgg
    ? `${food.grams} ${food.grams === 1 ? "uovo" : "uova"}`
    : `${food.grams}g`;

  doc.setFont("helvetica", "bold"); doc.setFontSize(8);
  const rightW = doc.getTextWidth(gLabel);

  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  setColor(doc, INK);
  const nameMaxW = w - rightW - 4;
  let n = displayName;
  while (doc.getTextWidth(n) > nameMaxW && n.length > 4) n = n.slice(0, -1);
  if (n !== displayName) n += "…";
  doc.text(n, x, y);
  if (m) {
    const baseW = doc.getTextWidth(n);
    doc.setFont("helvetica", "bold"); doc.setFontSize(5.5);
    doc.text("2", x + baseW + 0.4, y - 1.4);
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(8);
  setColor(doc, MUTED);
  doc.text(gLabel, x + w - rightW, y);
}

function drawLegend(doc, y) {
  setColor(doc, BORDER); doc.setLineWidth(0.2);
  doc.line(M_X, y - 3, M_X + CONTENT_W, y - 3);
  setColor(doc, MUTED); doc.setFont("helvetica", "bold"); doc.setFontSize(6.5);
  doc.text("LEGENDA", M_X, y + 1);
  const legends = [
    ["*", "cereali senza glutine"],
    ["**", "limitati al post-workout"],
    ["#", "anche a fine pasto"],
    ["²", "seconda scelta"],
  ];
  let cursorX = M_X + doc.getTextWidth("LEGENDA") + 4;
  legends.forEach(([sym, txt]) => {
    setColor(doc, INK); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
    doc.text(sym, cursorX, y + 1);
    const symW = doc.getTextWidth(sym);
    setColor(doc, MUTED); doc.setFont("helvetica", "normal"); doc.setFontSize(6.8);
    doc.text(txt, cursorX + symW + 1.2, y + 1);
    const txtW = doc.getTextWidth(txt);
    cursorX += symW + 1.2 + txtW + 6;
  });
}

// ------------------ main export ------------------
export function exportPorzionarioPdf({ foodDb, studio, gender } = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const blocks = [];
  CAT_ORDER.forEach((catKey, idx) => {
    const foods = foodDb?.foods?.[catKey] || [];
    if (foods.length === 0) return;
    blocks.push({ kind: "header", catKey, h: HEADER_H + CAT_TOP_PAD + CAT_BOTTOM_PAD });
    foods.forEach((f) => blocks.push({ kind: "row", catKey, food: f, h: LINE_H }));
    if (idx < CAT_ORDER.length - 1) blocks.push({ kind: "spacer", h: 2 });
  });

  const columns = [
    { x: M_X, items: [] },
    { x: M_X + COL_W + COL_GAP, items: [] },
  ];
  let colIdx = 0;
  let curY = TOP_Y;
  const fits = (h) => curY + h <= BOTTOM_Y;

  blocks.forEach((b) => {
    if (b.kind === "header") {
      const need = b.h + LINE_H * 3;
      if (!fits(need) && colIdx === 0) { colIdx = 1; curY = TOP_Y; }
    }
    if (!fits(b.h) && colIdx === 0) { colIdx = 1; curY = TOP_Y; }
    columns[colIdx].items.push(b);
    curY += b.h;
  });

  drawPageHeader(doc, { studio, gender });
  drawFooter(doc, { studio });

  columns.forEach((col) => {
    let y = TOP_Y;
    col.items.forEach((b) => {
      if (b.kind === "header") {
        y += CAT_TOP_PAD;
        drawCategoryHeader(doc, b.catKey, col.x, y, COL_W, gender);
        y += HEADER_H - CAT_TOP_PAD + CAT_BOTTOM_PAD;
      } else if (b.kind === "row") {
        drawFoodRow(doc, b.food, col.x + 2, y, COL_W - 2);
        y += LINE_H;
      } else if (b.kind === "spacer") {
        y += b.h;
      }
    });
  });

  drawLegend(doc, PAGE_H - LEGEND_H - FOOTER_H + 2);

  doc.save(`Porzionario_${gender === "F" ? "femminile" : "maschile"}.pdf`);
}
